import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { CriteriaEditPanelProps } from './CriteriaEditPanel';
import { buildCriteriaValues } from 'components/shared/CriteriaFieldsets';
import {
  CriteriaFormValues,
  SaveOutcome,
  useSaveCriteria,
} from 'concepts/Criteria/save/useSaveCriteria';
import { useCriteriaCache } from 'hooks/reviews/useCriteriaCache';
import { useConfirmUnsaved } from 'hooks/useConfirmUnsaved';
import { CriteriaFramework } from 'concepts/Criteria/framework/CriteriaFramework';

// The draft's own stage; the confirming values also carry which door raised
// the Unsaved changes modal. One stored variable, so contradictory
// combinations cannot be encoded.
type DraftLifecycleState =
  | 'dormant'
  | 'editing'
  | 'confirming-cancel'
  | 'confirming-close';

// How a draft ended: saved and cancelled return to view mode; closed also
// shuts the surface.
export type DraftOutcome = 'saved' | 'cancelled' | 'closed';

type Options = {
  onExit: (outcome: DraftOutcome) => void;
};

type useCriteriaDraftReturn = {
  isEditing: boolean;
  isDirty: boolean;
  isSaving: boolean;
  start: () => void;
  requestClose: () => void;
  panelProps: CriteriaEditPanelProps;
};

// Owns one draft's state in the host that outlives the presentation
// branch, so typed input survives the sub-800px dialog / 800px+ aside flip
// start() must run before the panel first renders.
export const useCriteriaDraft = ({
  onExit,
}: Options): useCriteriaDraftReturn => {
  const criteriaCache = useCriteriaCache();

  // The framework decides which category fields render. A conflicting save
  // adopts the server's framework so a re-save can't silently revert
  // another session's framework change - hence state, not the seed value.
  const [framework, setFramework] = useState<CriteriaFramework>();

  const form = useForm<CriteriaFormValues>({
    defaultValues: buildCriteriaValues(undefined, undefined),
  });

  const [lifecycleState, setLifecycleState] = useState<DraftLifecycleState>(
    'dormant'
  );

  const {
    saveCriteria,
    mergeGeneration,
    resetSave,
    dismissSave,
  } = useSaveCriteria({
    form,
    framework,
    requiresFramework: false,
    onConflictFrameworkAdopted: setFramework,
  });

  // handleSubmit swallows its callback's return value, so capture the
  // outcome aside; a validation refusal never runs the callback = 'failed'.
  const submit = async (): Promise<SaveOutcome> => {
    let outcome: SaveOutcome = 'failed';
    await form.handleSubmit(async (values) => {
      outcome = await saveCriteria(values);
    })();
    return outcome;
  };

  const { isDirty, isSubmitting } = form.formState;

  const [markClean, markDirty] = useConfirmUnsaved();
  useEffect(() => {
    (isDirty ? markDirty : markClean)();
  }, [isDirty, markClean, markDirty]);

  // Seeds once per draft, from the already-loaded criteria cache;
  // TanStack may background-refresh it mid-draft, but the seeded form
  // ignores it. A stale seed is safe: the save 409s and merges.
  const start = () => {
    const snapshot = criteriaCache.getSnapshot();
    setFramework(snapshot?.framework);
    form.reset(buildCriteriaValues(snapshot?.framework, snapshot?.criteria));
    resetSave(snapshot?.version);
    setLifecycleState('editing');
  };

  // The single exit door. Resetting also drops isDirty, which disarms the
  // beforeunload guard via the effect above; markClean besides, so the
  // disarm is synchronous.
  const exitDraft = (outcome: DraftOutcome) => {
    form.reset(buildCriteriaValues(undefined, undefined));
    markClean();
    dismissSave();
    setLifecycleState('dormant');
    onExit(outcome);
  };

  const requestClose = () => {
    if (isSubmitting) return;
    if (isDirty) {
      setLifecycleState('confirming-close');
    } else {
      exitDraft('closed');
    }
  };

  const onCancel = () => {
    if (isDirty) {
      setLifecycleState('confirming-cancel');
    } else {
      exitDraft('cancelled');
    }
  };

  const onSave = async () => {
    if ((await submit()) === 'saved') exitDraft('saved');
  };

  // A save from the Cancel route takes the ordinary saved path back to view
  // mode; only the close route shuts the surface on success.
  const onModalSave = async () => {
    const door = lifecycleState;
    setLifecycleState('editing');
    if ((await submit()) === 'saved') {
      exitDraft(door === 'confirming-close' ? 'closed' : 'saved');
    }
  };

  const onModalDiscard = () => {
    const door = lifecycleState;
    exitDraft(door === 'confirming-cancel' ? 'cancelled' : 'closed');
  };

  const panelProps: CriteriaEditPanelProps = {
    form,
    framework,
    mergeGeneration,
    onSave,
    onCancel,
    unsavedChanges: {
      isOpen:
        lifecycleState === 'confirming-cancel' ||
        lifecycleState === 'confirming-close',
      onSave: onModalSave,
      onDiscard: onModalDiscard,
      onKeepEditing: () => setLifecycleState('editing'),
    },
  };

  return {
    isEditing: lifecycleState !== 'dormant',
    isDirty,
    isSaving: isSubmitting,
    start,
    requestClose,
    panelProps,
  };
};
